import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import { useAuth } from '../../../contexts/AuthContext';

const SubscriptionPayment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signUp } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Get form data from previous step
  const previousData = location?.state?.formData || {};

  const [formData, setFormData] = useState({
    subscriptionPlan: '',
    paymentMethod: '',
    agreeToTerms: false,
    agreeToPrivacy: false,
    // Zelle specific
    zelleEmail: '',
    zellePhone: '',
    zelleConfirmationNumber: ''
  });

  const subscriptionPlans = [
    {
      id: 'starter',
      name: 'Starter',
      price: 49,
      billingCycle: 'month',
      trialDays: 7,
      description: 'Perfect for single gym owners',
      features: [
        'Single gym location',
        'Up to 500 members',
        'Basic reporting',
        'Email support',
        'Member check-in',
        'Class scheduling',
        'Staff management (up to 5)'
      ]
    },
    {
      id: 'professional',
      name: 'Professional',
      price: 149,
      billingCycle: 'month',
      trialDays: 7,
      description: 'For growing gym businesses',
      popular: true,
      features: [
        '2-5 gym locations',
        'Up to 2,000 members',
        'Advanced analytics',
        'Priority support',
        'All Starter features',
        'Equipment management',
        'Financial reconciliation',
        'Staff performance tracking'
      ]
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: 399,
      billingCycle: 'month',
      trialDays: 7,
      description: 'For multi-location gym chains',
      features: [
        '6+ gym locations',
        'Unlimited members',
        'Custom reporting',
        'Dedicated support',
        'All Professional features',
        'Multi-location analytics',
        'Custom integrations',
        'API access',
        'White-label options'
      ]
    }
  ];

  const paymentMethods = [
    {
      id: 'stripe',
      name: 'Credit/Debit Card',
      icon: 'CreditCard',
      description: 'Instant activation with Stripe',
      status: 'automatic'
    },
    {
      id: 'paypal',
      name: 'PayPal',
      icon: 'Wallet',
      description: 'Pay securely with PayPal',
      status: 'automatic'
    },
    {
      id: 'zelle',
      name: 'Zelle',
      icon: 'Smartphone',
      description: 'Manual approval required',
      status: 'manual'
    }
  ];

  const selectedPlan = subscriptionPlans?.find(plan => plan?.id === formData?.subscriptionPlan);
  const selectedPaymentMethod = paymentMethods?.find(method => method?.id === formData?.paymentMethod);

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError('');
  };

  const handlePlanSelect = (planId) => {
    setFormData(prev => ({ ...prev, subscriptionPlan: planId }));
    setError('');
  };

  const handlePaymentMethodSelect = (methodId) => {
    setFormData(prev => ({ ...prev, paymentMethod: methodId }));
    setError('');
  };

  const validateForm = () => {
    if (!formData?.subscriptionPlan) {
      setError('Please select a subscription plan');
      return false;
    }
    if (!formData?.paymentMethod) {
      setError('Please select a payment method');
      return false;
    }
    if (formData?.paymentMethod === 'zelle') {
      if (!formData?.zelleEmail && !formData?.zellePhone) {
        setError('Please provide Zelle email or phone number');
        return false;
      }
      if (!formData?.zelleConfirmationNumber?.trim()) {
        setError('Please provide Zelle confirmation number');
        return false;
      }
    }
    if (!formData?.agreeToTerms) {
      setError('You must agree to the Terms of Service');
      return false;
    }
    if (!formData?.agreeToPrivacy) {
      setError('You must agree to the Privacy Policy');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    // Check if we have previous data
    if (!previousData?.email || !previousData?.password) {
      setError('Missing registration information. Please start from the beginning.');
      return;
    }

    setLoading(true);

    try {
      // For Stripe and PayPal, we would normally process payment here
      // For now, we'll create the account with pending payment status
      const accountStatus = formData?.paymentMethod === 'zelle' ? 'pending_approval' : 'active';

      const { data, error: signUpError } = await signUp(
        previousData?.email,
        previousData?.password,
        {
          first_name: previousData?.firstName,
          last_name: previousData?.lastName,
          full_name: `${previousData?.firstName} ${previousData?.lastName}`,
          role: 'owner',
          phone: previousData?.phone,
          gym_name: previousData?.gymName,
          gym_address: previousData?.gymAddress,
          number_of_locations: previousData?.numberOfLocations,
          subscription_plan: formData?.subscriptionPlan,
          payment_method: formData?.paymentMethod,
          trial_days: 7,
          account_status: accountStatus,
          ...(formData?.paymentMethod === 'zelle' && {
            zelle_email: formData?.zelleEmail,
            zelle_phone: formData?.zellePhone,
            zelle_confirmation: formData?.zelleConfirmationNumber
          })
        }
      );

      if (signUpError) {
        setError(signUpError?.message || 'Failed to create account. Please try again.');
        setLoading(false);
        return;
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 4000);
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/gym-owner-signup', { state: { formData: previousData } });
  };

  if (success) {
    return (
      <>
        <Helmet>
          <title>Registration Successful - TrainaryGym</title>
        </Helmet>
        <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-primary/5 to-success/5">
          <div className="card max-w-md w-full text-center space-y-6">
            <div className="w-16 h-16 mx-auto rounded-full bg-success/10 flex items-center justify-center">
              <Icon name="CheckCircle" size={32} color="var(--color-success)" />
            </div>
            <div className="space-y-2">
              <h2 className="text-2xl font-heading font-bold text-foreground">Registration Successful!</h2>
              <p className="text-muted-foreground">
                {formData?.paymentMethod === 'zelle' ?'Your account is pending approval. We will verify your Zelle payment and activate your account within 24-48 hours.' :'Your gym owner account has been created with a 7-day free trial. Please check your email to verify your account.'}
              </p>
            </div>
            {selectedPlan && (
              <div className="bg-primary/5 rounded-lg p-4 border border-primary/20">
                <p className="text-sm text-muted-foreground mb-2">Your Plan</p>
                <p className="text-lg font-semibold text-foreground">{selectedPlan?.name}</p>
                <p className="text-2xl font-bold text-primary">
                  ${selectedPlan?.price}
                  <span className="text-sm text-muted-foreground">/{selectedPlan?.billingCycle}</span>
                </p>
                <p className="text-sm text-success mt-2">✓ 7-day free trial included</p>
              </div>
            )}
            <p className="text-sm text-muted-foreground">
              Redirecting to login page...
            </p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Subscription & Payment - TrainaryGym</title>
        <meta name="description" content="Choose your subscription plan and complete payment to start managing your gym with TrainaryGym." />
      </Helmet>
      <div className="min-h-screen flex items-center justify-center p-4 md:p-6 lg:p-8 bg-gradient-to-br from-primary/5 to-accent/5">
        <div className="w-full max-w-6xl">
          {/* Header */}
          <div className="text-center mb-8">
            <button
              onClick={handleBack}
              className="inline-flex items-center gap-2 mb-6 text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name="ArrowLeft" size={20} />
              <span className="text-sm">Back to Registration</span>
            </button>
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Icon name="Building2" size={24} color="var(--color-primary)" />
              </div>
              <h1 className="text-3xl font-heading font-bold text-foreground">TrainaryGym</h1>
            </div>
            <h2 className="text-2xl font-heading font-semibold text-foreground mb-2">
              Choose Your Plan & Payment
            </h2>
            <p className="text-muted-foreground">
              Start with a 7-day free trial • No credit card required for trial
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Error Message */}
            {error && (
              <div className="card p-4 bg-destructive/10 border-destructive/20 flex items-start gap-3">
                <Icon name="AlertCircle" size={20} color="var(--color-destructive)" />
                <p className="text-sm text-destructive flex-1">{error}</p>
              </div>
            )}

            {/* Subscription Plans */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <Icon name="Package" size={24} color="var(--color-primary)" />
                <h3 className="text-xl font-semibold text-foreground">Select Your Subscription Plan</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {subscriptionPlans?.map((plan) => (
                  <button
                    key={plan?.id}
                    type="button"
                    onClick={() => handlePlanSelect(plan?.id)}
                    className={`card text-left transition-all duration-200 relative ${
                      formData?.subscriptionPlan === plan?.id
                        ? 'border-primary border-2 shadow-lg'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    {plan?.popular && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <span className="bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">
                          Most Popular
                        </span>
                      </div>
                    )}
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-lg font-semibold text-foreground">{plan?.name}</h4>
                        <p className="text-sm text-muted-foreground mt-1">{plan?.description}</p>
                      </div>
                      <div>
                        <div className="flex items-baseline gap-1">
                          <span className="text-3xl font-bold text-foreground">${plan?.price}</span>
                          <span className="text-muted-foreground">/{plan?.billingCycle}</span>
                        </div>
                        <p className="text-sm text-success mt-1 flex items-center gap-1">
                          <Icon name="Gift" size={14} />
                          {plan?.trialDays}-day free trial
                        </p>
                      </div>
                      <ul className="space-y-2">
                        {plan?.features?.map((feature, index) => (
                          <li key={index} className="flex items-start gap-2 text-sm">
                            <Icon name="Check" size={16} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                            <span className="text-muted-foreground">{feature}</span>
                          </li>
                        ))}
                      </ul>
                      {formData?.subscriptionPlan === plan?.id && (
                        <div className="flex items-center justify-center gap-2 text-primary font-medium">
                          <Icon name="CheckCircle2" size={20} />
                          <span>Selected</span>
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Payment Methods */}
            {formData?.subscriptionPlan && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="CreditCard" size={24} color="var(--color-primary)" />
                  <h3 className="text-xl font-semibold text-foreground">Select Payment Method</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {paymentMethods?.map((method) => (
                    <button
                      key={method?.id}
                      type="button"
                      onClick={() => handlePaymentMethodSelect(method?.id)}
                      className={`card text-left transition-all duration-200 ${
                        formData?.paymentMethod === method?.id
                          ? 'border-primary border-2' :'border-border hover:border-primary/50'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          formData?.paymentMethod === method?.id ? 'bg-primary/10' : 'bg-muted'
                        }`}>
                          <Icon
                            name={method?.icon}
                            size={24}
                            color={formData?.paymentMethod === method?.id ? 'var(--color-primary)' : 'var(--color-muted-foreground)'}
                          />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-foreground">{method?.name}</h4>
                          <p className="text-sm text-muted-foreground mt-1">{method?.description}</p>
                          {method?.status === 'manual' && (
                            <span className="inline-flex items-center gap-1 text-xs text-warning mt-2">
                              <Icon name="Clock" size={12} />
                              Manual approval
                            </span>
                          )}
                        </div>
                        {formData?.paymentMethod === method?.id && (
                          <Icon name="CheckCircle2" size={20} color="var(--color-primary)" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>

                {/* Zelle Payment Details */}
                {formData?.paymentMethod === 'zelle' && (
                  <div className="card bg-warning/5 border-warning/20">
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <Icon name="Info" size={20} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-semibold text-foreground mb-2">Zelle Payment Instructions</h4>
                          <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
                            <li>Send payment to: <strong className="text-foreground">info@morethantechnologyllc.com</strong></li>
                            <li>Amount: <strong className="text-foreground">${selectedPlan?.price}</strong></li>
                            <li>Include your email in the payment note</li>
                            <li>Enter your Zelle details and confirmation number below</li>
                          </ol>
                        </div>
                      </div>
                      <div className="space-y-4 pt-4 border-t border-border">
                        <Input
                          label="Zelle Email (if used)"
                          type="email"
                          name="zelleEmail"
                          value={formData?.zelleEmail}
                          onChange={handleChange}
                          placeholder="your-email@example.com"
                        />
                        <Input
                          label="Zelle Phone (if used)"
                          type="tel"
                          name="zellePhone"
                          value={formData?.zellePhone}
                          onChange={handleChange}
                          placeholder="(555) 123-4567"
                        />
                        <Input
                          label="Zelle Confirmation Number"
                          type="text"
                          name="zelleConfirmationNumber"
                          value={formData?.zelleConfirmationNumber}
                          onChange={handleChange}
                          placeholder="Enter confirmation number from Zelle"
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Stripe/PayPal Notice */}
                {(formData?.paymentMethod === 'stripe' || formData?.paymentMethod === 'paypal') && (
                  <div className="card bg-primary/5 border-primary/20">
                    <div className="flex items-start gap-3">
                      <Icon name="Shield" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-foreground mb-2">
                          {formData?.paymentMethod === 'stripe' ? 'Secure Card Payment' : 'PayPal Payment'}
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          {formData?.paymentMethod === 'stripe' ?'You will be redirected to our secure payment page to enter your card details. Your trial starts immediately, and you will not be charged until after 7 days.' :'You will be redirected to PayPal to complete your payment securely. Your trial starts immediately.'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Terms and Privacy Policy */}
            {formData?.paymentMethod && (
              <div className="card">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                    <Icon name="FileText" size={20} color="var(--color-primary)" />
                    Terms & Conditions
                  </h3>
                  <div className="space-y-4 pt-4 border-t border-border">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        name="agreeToTerms"
                        checked={formData?.agreeToTerms}
                        onChange={handleChange}
                      />
                      <label className="text-sm text-muted-foreground cursor-pointer" onClick={() => setFormData(prev => ({ ...prev, agreeToTerms: !prev?.agreeToTerms }))}>
                        I agree to the{' '}
                        <Link to="/terms-and-conditions" className="text-primary hover:text-primary/80 transition-colors duration-250 focus-ring rounded px-1" target="_blank">
                          Terms and Conditions
                        </Link>
                      </label>
                    </div>
                    <div className="flex items-start gap-3">
                      <Checkbox
                        name="agreeToPrivacy"
                        checked={formData?.agreeToPrivacy}
                        onChange={handleChange}
                      />
                      <label className="text-sm text-muted-foreground cursor-pointer" onClick={() => setFormData(prev => ({ ...prev, agreeToPrivacy: !prev?.agreeToPrivacy }))}>
                        I have read and accept the{' '}
                        <Link to="/privacy-policy" className="text-primary hover:text-primary/80 transition-colors duration-250 focus-ring rounded px-1" target="_blank">
                          Privacy Policy
                        </Link>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            {formData?.paymentMethod && (
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleBack}
                  className="flex-1"
                  iconName="ArrowLeft"
                  iconPosition="left"
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  disabled={loading || !formData?.agreeToTerms || !formData?.agreeToPrivacy}
                  className="flex-1"
                  iconName={loading ? 'Loader2' : 'CheckCircle'}
                  iconClassName={loading ? 'animate-spin' : ''}
                  iconPosition="left"
                >
                  {loading ? 'Creating Account...' : 'Create Gym Owner Account'}
                </Button>
              </div>
            )}
          </form>
        </div>
      </div>
    </>
  );
};

export default SubscriptionPayment;